
import { useCallback, useRef } from "react";
import toast from "react-hot-toast";
import { api } from "@/services/api";
import { useAgentStore } from "@/store/agentStore";
import type { ConversationTurn } from "@/services/api";

export function useAnalytics() {
  const closeStream = useRef<(() => void) | null>(null);
  const { addUserMessage, startRun, nodeStarted, nodeDone, runComplete, runFailed, runClarification, clearPendingReport, setChatSessionId, setSavedSessions } = useAgentStore();
  const dataSource = useAgentStore((s) => s.dataSource);
  const uploadedFile = useAgentStore((s) => s.uploadedFile);
  const pendingReportId = useAgentStore((s) => s.pendingReportId);
  const warehouseId = useAgentStore((s) => s.warehouseId);
  const chatSessionId = useAgentStore((s) => s.chatSessionId);

  const submit = useCallback(
    async (query: string) => {
      // Capture history BEFORE adding the new user message
      const priorMessages = useAgentStore.getState().messages;
      const conversationHistory: ConversationTurn[] = priorMessages
        .filter((msg) => !msg.isLoading)
        .map((msg) => {
          if (msg.type === "user") {
            return { role: "user" as const, content: msg.content };
          }
          const result = msg.result;
          let content = result?.summary || msg.content;
          // Include the original query so the LLM knows what data was analyzed
          if (result?.query) {
            content = `[Analyzed: "${result.query}"] ${content}`;
          }
          // Include chart types so the LLM knows what visualization was shown
          if (result?.charts?.length) {
            const types = [...new Set(result.charts.map((c) => c.chart_type).filter(Boolean))].join(", ");
            if (types) content += ` [Charts displayed: ${types}]`;
          }
          return { role: "assistant" as const, content };
        })
        .slice(-10); // keep last 5 turns (10 messages)

      closeStream.current?.();
      addUserMessage(query);

      const toastId = toast.loading("Analyzing…");

      try {
        // 1. POST to /analyze (pass current chat session so backend appends to it)
        const reportId = pendingReportId ?? undefined;
        clearPendingReport();
        const currentChatSessionId = useAgentStore.getState().chatSessionId;
        const currentUserId = useAgentStore.getState().userId;
        const { session_id, chat_session_id } = await api.analyze({
          query,
          data_source: dataSource,
          file_id: uploadedFile?.file_id,
          report_id: reportId,
          warehouse_id: warehouseId ?? undefined,
          conversation_history: conversationHistory,
          chat_session_id: currentChatSessionId ?? undefined,
          user_id: currentUserId ?? undefined,
        });
        setChatSessionId(chat_session_id);
        startRun(session_id, query);
        toast.dismiss(toastId);

        // 2. Open SSE stream
        closeStream.current = api.streamEvents(session_id, {
          onNodeStart(e) {
            nodeStarted(e);
          },

          onNodeDone(e) {
            nodeDone(e);
          },

          async onComplete() {
            // 3. Fetch full result once agent finishes
            try {
              // Retry up to 5× — result may not be cached yet
              let result = null;
              for (let i = 0; i < 5; i++) {
                try {
                  result = await api.getResult(session_id);
                  break;
                } catch {
                  await delay(600);
                }
              }
              if (!result) throw new Error("Result unavailable after retries");
              runComplete(result);
              toast.success("Analysis complete!");

              // Refresh the sessions list so the sidebar stays current
              const state = useAgentStore.getState();
              api.listSessions({
                userId: state.userId ?? undefined,
                warehouseId: state.warehouseId ?? undefined,
              }).then(setSavedSessions).catch(() => {});
            } catch (err: any) {
              runFailed(err.message);
              toast.error(err.message);
            }
          },

          onClarification(e) {
            runClarification(e.questions);
            toast.dismiss(toastId);
          },

          onError(msg) {
            runFailed(msg);
            toast.error(`Error: ${msg}`);
          },
        });
      } catch (err: any) {
        toast.error(err.message);
      }
    },
    [addUserMessage, startRun, nodeStarted, nodeDone, runComplete, runFailed, runClarification, clearPendingReport, setChatSessionId, setSavedSessions, dataSource, uploadedFile, pendingReportId, warehouseId, chatSessionId]
  );

  const cancel = useCallback(() => {
    closeStream.current?.();
  }, []);

  return { submit, cancel };
}

function delay(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}
